import React from 'react'

import {
    useDeleteWorkExperienceV2ApplyMutation,
    usePostWorkExperienceV2ApplyMutation,
    usePutWorkExperienceV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2Card from '@core/components/ApplyDialogV2/ApplyV2Card'
import ApplyV2NoFieldsSwitch from '@core/components/ApplyDialogV2/ApplyV2NoFieldsSwitch'
import ApplyV2SkipNext from '@core/components/ApplyDialogV2/ApplyV2SkipNext'
import ApplyV2WorkExperienceStep2Add from '@core/components/ApplyDialogV2/ApplyV2WorkExperienceStep/ApplyV2WorkExperienceStep2/ApplyV2WorkExperienceStep2Add'
import ApplyV2WorkExperienceStep2Edit from '@core/components/ApplyDialogV2/ApplyV2WorkExperienceStep/ApplyV2WorkExperienceStep2/ApplyV2WorkExperienceStep2Edit'
import { EducationIcon } from '@core/components/ApplyDialogV2/ApplyV2WorkExperienceStep/icons/icon'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import './ApplyV2WorkExperienceStep2.scss'

const baseClass = 'ApplyV2WorkExperienceStep2'

function ApplyV2WorkExperienceStep2() {
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        isFinalStep,
        setIsFinalStep,
        isPostApplied,
        onCloseApply,
        settingsData,
        navItems,
        onNextStep,
        postProfileRefetch,
    } = useApplyV2Context()
    const { getTitle } = useApplyV2NavTranslations()
    const [itemLoading, setItemLoading] = React.useState<string | number | null>(null)
    const [editState, setEditState] = React.useState<'add' | string | null>(null)
    const [isNoItems, setIsNoItems] = React.useState(false)
    const [postWork, { isLoading: isPostLoading }] = usePostWorkExperienceV2ApplyMutation()
    const [putWork, { isLoading: isPutLoading }] = usePutWorkExperienceV2ApplyMutation()
    const [deleteWork, { isLoading: isDeleteLoading }] = useDeleteWorkExperienceV2ApplyMutation()

    // const experiences = [...(postProfileData?.data?.workExperience ?? [])]
    const experiences = [...(postProfileData?.data?.workExperience ?? [])].sort((a, b) => {
        if (a.current && !b.current) return -1
        if (!a.current && b.current) return 1

        const getSortValue = (item: any) => {
            if (item.current) return Infinity

            if (!item.fromDate) return 0

            if (item.fromDate === '0000-00-00') {
                return 0
            }

            if (item.fromDate.includes('-00-') || item.fromDate.endsWith('-00')) {
                const yearPart = item.fromDate.split('-')[0]
                const year = parseInt(yearPart)
                if (!isNaN(year) && year > 0) {
                    return new Date(year, 0, 1).getTime() // 1 січня
                }
                return 0
            }

            try {
                const date = new Date(item.fromDate)
                return isNaN(date.getTime()) ? 0 : date.getTime()
            } catch {
                return 0
            }
        }

        const valueA = getSortValue(a)
        const valueB = getSortValue(b)

        return valueB - valueA
    })

    const handleDeleteCourseRequest = async (uid: string) => {
        try {
            setItemLoading(uid)
            await deleteWork({ uid: postProfileData?.data?.uid || '', workUid: uid }).unwrap()
            await postProfileRefetch()
        } catch (error) {
            console.log('Error deleting experience:', error)
        } finally {
            setItemLoading(null)
        }
    }

    const getIsLoading = (uid?: string | number | null) => {
        return itemLoading === uid
    }

    const updatedCourse = experiences.find((c) => c.uid === editState || c.existingId === editState)

    if (editState === 'add') {
        return <ApplyV2WorkExperienceStep2Add onClose={() => setEditState(null)} />
    }
    if (editState && editState.length > 3 && editState !== 'add' && updatedCourse) {
        return <ApplyV2WorkExperienceStep2Edit onClose={() => setEditState(null)} item={updatedCourse} />
    }

    return (
        <div className={baseClass}>
            <div className={`${baseClass}__container`}>
                {experiences.length === 0 ? (
                    <div className={`${baseClass}__items ${baseClass}__empty`}>
                        <div className={` ${baseClass}__empty--icon`}>
                            <EducationIcon />
                        </div>
                        <div className={` ${baseClass}__empty--desc`}>
                            <div className={` ${baseClass}__empty--desc-title`}>
                                {isNoItems
                                    ? getTitle('10302', "I don't have any work experience")
                                    : getTitle('10301', 'No work experience added yet')}
                            </div>
                            <div className={` ${baseClass}__empty--desc-btns`}>
                                <ApplyV2Button
                                    variant={'outline'}
                                    size={'small'}
                                    className={` ${baseClass}__empty--desc-btns--add`}
                                    onClick={() => {
                                        setEditState('add')
                                    }}
                                    isDisabled={isNoItems}
                                    leftIcon={<i className="fa-regular fa-plus"></i>}
                                >
                                    {getTitle('10300', 'Add experience')}
                                </ApplyV2Button>
                                {/*<ApplyV2Switch*/}
                                {/*    checked={isNoItems}*/}
                                {/*    onChange={(e) => {*/}
                                {/*        setIsNoItems(e.target.checked)*/}
                                {/*        // if (e.target.checked) {*/}
                                {/*        //     setEditState(null)*/}
                                {/*        // } else {*/}
                                {/*        //     setEditState('add')*/}
                                {/*        // }*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    {getTitle('9557', 'No items')}*/}
                                {/*</ApplyV2Switch>*/}
                                <ApplyV2NoFieldsSwitch
                                    className={`${baseClass}__empty--desc-switch`}
                                    checked={isNoItems}
                                    onChange={(v) => setIsNoItems(v)}
                                    label={getTitle('10303', 'No experience')}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={`${baseClass}__items`}>
                        {experiences.map((item) => (
                            <ApplySkeleton
                                isLoading={getIsLoading(item.uid || item.existingId)}
                                key={item.uid ?? item.existingId}
                                style={{
                                    height: '150px',
                                    marginBottom: '1rem',
                                    display: 'block',
                                    width: '100%',
                                }}
                            >
                                <ApplyV2Card
                                    // key={item.uid ?? item.existingId}
                                    className={`${baseClass}__items--course`}
                                    title={decodeHtmlEntitiesRecursive(item.companyName)}
                                    headerDescription={decodeHtmlEntitiesRecursive(item.title)}
                                    localeSection={item.location}
                                    onDelete={() => {
                                        handleDeleteCourseRequest(item.uid)
                                    }}
                                    onEdit={() => setEditState(item.uid)}
                                    date={{
                                        startDate: item.fromDate,
                                        endDate: item.toDate,
                                        dateFormat: 'MMMM YYYY',
                                    }}
                                >
                                    {item.description}
                                </ApplyV2Card>
                            </ApplySkeleton>
                        ))}
                        {isPostProfileLoading && !itemLoading && (
                            <ApplySkeleton
                                isLoading={isPostProfileLoading}
                                style={{
                                    height: '150px',
                                    marginBottom: '1rem',
                                    display: 'block',
                                    width: '100%',
                                }}
                            />
                        )}
                        <ApplyV2Button
                            variant={'transparent'}
                            className={`${baseClass}__addBtn`}
                            onClick={() => {
                                setEditState('add')
                            }}
                            leftIcon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M7.99967 1.33301C8.36786 1.33301 8.66634 1.63148 8.66634 1.99967V7.12611H13.9997C14.3679 7.12611 14.6663 7.42459 14.6663 7.79278C14.6663 8.16097 14.3679 8.45944 13.9997 8.45944H8.66634V13.9997C8.66634 14.3679 8.36786 14.6663 7.99967 14.6663C7.63148 14.6663 7.33301 14.3679 7.33301 13.9997V8.45944H1.99967C1.63148 8.45944 1.33301 8.16097 1.33301 7.79278C1.33301 7.42459 1.63148 7.12611 1.99967 7.12611H7.33301V1.99967C7.33301 1.63148 7.63148 1.33301 7.99967 1.33301Z"
                                        fill="#19191A"
                                    />
                                </svg>
                            }
                        >
                            {getTitle('10300', 'Add experience')}
                        </ApplyV2Button>
                    </div>
                )}
            </div>
            <div className={`ApplyDialogV2__next ${baseClass}__next`}>
                {!settingsData?.steps?.workExperience?.required && (
                    <ApplyV2Button
                        variant={'transparent'}
                        className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-skip`}
                        rightIcon={'>'}
                        // isDisabled={!isValid || isLoading}
                        onClick={() => {
                            onNextStep()
                        }}
                    >
                        {getTitle('6107', 'Skip')}
                    </ApplyV2Button>
                )}
                <ApplyV2Button
                    variant={'primary'}
                    className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-submit`}
                    onClick={onNextStep}
                    isLoading={isPostApplied || isPostProfileLoading}
                    isDisabled={isPostApplied || isPostProfileLoading || (!isNoItems && experiences.length === 0)}
                    type={'submit'}
                >
                    {getTitle('3035', 'Next')}
                </ApplyV2Button>
            </div>
            {/*<ApplyV2SkipNext />*/}
        </div>
    )
}

export default ApplyV2WorkExperienceStep2
